import { useRouteQuery } from '@/common/route/useRouteQuery';
import { pageSize } from '@/common/utils/helpers';
import { useBaseListView } from '@/common/view/useBaseListView';
import { useCrudActions } from '@/common/view/useCrudActions';
import { useVModal } from '@/plugins/modal/v-modal';
import { avatarService } from '@/services/avatar';
import useVuelidate from '@vuelidate/core';
import { minLength } from '@vuelidate/validators';
import { reactive } from 'vue';

const BUNDLE_LINK_MODAL_NAME = 'item.link.bundle';

export function useAvatarItemList() {
    const vModal = useVModal();
    const { executeAction, excuteAndNotify, confirmAndRemove } = useCrudActions();

    const bundleSelection = reactive({
        selectedItemId: null,
        linkableBundles: []
    });

    const { query, updateQueryToRoute } = useRouteQuery({
        definition: {
            categoryId: { type: 'number', default: null },
            name: { type: 'string', default: '' },
            isNew: { type: 'string', default: '' },
            offset: { type: 'number', default: 0 },
            limit: { type: 'number', default: pageSize }
        },
        onChange: () => fetchList()
    });

    const validations = {
        name: { min: minLength(3) }
    };

    const v$ = useVuelidate(validations, query);

    const { list, total, fetchList } = useBaseListView({
        request: avatarService.item.list,
        paramsBuilder: () => ({ ...query }),
        hooks: {
            onPageChange: offset => (query.offset = offset),
            onSuccess: updateQueryToRoute,
            canFetch: () => {
                v$.value.$touch();
                return !v$.value.$invalid;
            }
        }
    });

    const actions = {
        remove: confirmAndRemove({
            request: avatarService.item.remove,
            onSuccess: () => fetchList()
        }),
        linkBundle: excuteAndNotify({
            canExecute: () => Boolean(bundleSelection.selectedItemId) && bundleSelection.linkableBundles.length > 0,
            request: () => {
                const bundleIds = bundleSelection.linkableBundles.filter(b => b.hasLinked).map(b => b.id);
                return avatarService.bundle.updateForItem(bundleSelection.selectedItemId, bundleIds.join(','));
            },
            onFinally: () => {
                bundleSelection.selectedItemId = null;
                bundleSelection.linkableBundles = [];
                vModal.close({ name: BUNDLE_LINK_MODAL_NAME });
            }
        }),
        openLinkBundle: (id: number) =>
            executeAction({
                request: () => avatarService.bundle.getListForItem(id),
                onSuccess: ({ data }) => {
                    bundleSelection.selectedItemId = id;
                    bundleSelection.linkableBundles = data;
                    vModal.open({ name: BUNDLE_LINK_MODAL_NAME });
                }
            })(),
        closeLinkBundle: () => {
            bundleSelection.selectedItemId = null;
            bundleSelection.linkableBundles = [];
            vModal.close({ name: BUNDLE_LINK_MODAL_NAME });
        }
    };

    return { v$, query, list, total, fetchList, actions, bundleSelection, BUNDLE_LINK_MODAL_NAME };
}
